import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { get, filter, includes } from 'lodash'
import { FormikDropdownField } from 'components/form/Dropdown'
import { FormikInputField } from 'components/form/InputField'
import { FormikMultiSelectDropdownField } from 'components/form/Dropdown'

import {
  Grid,
  Typography,
  makeStyles,
  Accordion,
  AccordionDetails,
  AccordionSummary
} from '@material-ui/core'
import { Card } from 'components/layout/Card'
import { Separator } from 'components/layout/Separator'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import Alert from '@material-ui/lab/Alert'
import urlSlug from 'url-slug'
import { adminCoreBusinessList } from 'admin/components/core_businesses/actions/admin_core_business'
import { adminPublishedTagList } from 'admin/components/tags/actions/admin_tag'
import {
  adminBeneficiaryVettingStates,
  adminPopulationGroups,
  adminDistributionChannels
} from 'admin/actions/admin_dropdown_options'
import BeneficiaryVettingNpoDssFields from './BeneficiaryVettingNpoDssFields'
import BeneficiaryVettingResourcingCapacityFields from './BeneficiaryVettingResourcingCapacityFields'
import { FormikSwitchField } from 'components/form/SwitchField'
import BeneficiaryVettingFeedingFields from './BeneficiaryVettingFeedingFields'
import BeneficiaryVettingHealthSafetyFields from './BeneficiaryVettingHealthSafetyFields'
import * as Yup from 'yup'

const ALLOWED_VETTING_STATUS_VALUES = ['vetting_in_progress', 'declined']

const useStyles = makeStyles((theme) => ({
  logo: {
    padding: '7px 0px',
    marginTop: 15
  },
  heading: {
    fontSize: 18,
    fontWeight: 500
  },
  companyAccordion: {
    marginTop: 15
  },
  fieldDescription: {
    fontSize: 12,
    color: 'rgba(0, 0, 0, 0.54)',
    marginTop: -4,
    marginBottom: 4
  }
}))

export const validationVettingSchema = Yup.object().shape({
  company: Yup.object().shape({
    // Details
    name: Yup.string().required('Required'),
    core_businesses: Yup.string().required('Required'),
    target_population: Yup.array().required('Required').min(1, 'Select at least one target population'),
    programmes_and_services: Yup.string().required('Required'),
    distribution_channel: Yup.array().required('Required').min(1, 'Select at least one distribution channel'),
    // Feeding
    meals_provided: Yup.array().required('Required').min(1, 'Select at least one meal type'),
    days_of_week: Yup.array().required('Required').min(1, 'Select at least one day'),
    feeding_history_last_date: Yup.string().required('Required'),
    feeding_frequency: Yup.string().required('Required'),
    number_of_people_served: Yup.number()
      .nullable()
      .transform((v, o) => (o === '' || o === null || (typeof v === 'number' && Number.isNaN(v)) ? undefined : v))
      .min(0, 'Must be 0 or more')
      .required('Required'),
    // Health and Safety
    food_storage_adequate: Yup.array().required('Required').min(1, 'Select at least one food storage option'),
    infrastructure_checks_adequate: Yup.array().required('Required').min(1, 'Select at least one infrastructure check'),
    // Resourcing & capacity
    full_time_females: Yup.number()
      .nullable()
      .transform((v, o) => (o === '' || o === null || (typeof v === 'number' && Number.isNaN(v)) ? undefined : v))
      .min(0, 'Must be 0 or more')
      .required('Required'),
    full_time_males: Yup.number()
      .nullable()
      .transform((v, o) => (o === '' || o === null || (typeof v === 'number' && Number.isNaN(v)) ? undefined : v))
      .min(0, 'Must be 0 or more')
      .required('Required'),
    number_of_volunteers: Yup.number()
      .nullable()
      .transform((v, o) => (o === '' || o === null || (typeof v === 'number' && Number.isNaN(v)) ? undefined : v))
      .min(0, 'Must be 0 or more')
      .required('Required'),
    reliance_on_sa_harvest: Yup.string().required('Required'),
    transport_capacity: Yup.string().required('Required'),
    proposal_writing_capabilities: Yup.string().required('Required'),
    digital_capabilities: Yup.string().required('Required'),
    // NPO/DSS
    npo_number: Yup.string().when('is_registered_npo', {
      is: true,
      then: Yup.string().nullable().required('NPO number is required when registered as NPO'),
      otherwise: Yup.string().nullable()
    }),
    dsd_registration_number: Yup.string().when('is_registered_with_dsd', {
      is: true,
      then: Yup.string().nullable().required('DSD registration number is required when registered with DSD'),
      otherwise: Yup.string().nullable()
    }),
  })
})

export default function AdminBeneficiaryVettingDetailsFormExtended(props) {
  const { formik_props, company_id, in_drawer = false } = props
  const dispatch = useDispatch()
  const classes = useStyles()
  const values = formik_props.values
  const [expanded, setExpanded] = React.useState('panel1')
  const company = get(values, 'company', {})
  const status = get(formik_props.values, 'status')
  const fieldsDisabled = status !== 'vetting_in_progress'


  const allStatusOptions = useSelector(() => adminBeneficiaryVettingStates.getAsOptions())
  const status_options = filter(allStatusOptions, (opt) => includes(ALLOWED_VETTING_STATUS_VALUES, opt.value))
  const coreBusinessOptions = useSelector(() => adminCoreBusinessList.getAsOptions())
  const targetPopulationOptions = useSelector(() => adminPopulationGroups.getAsOptions())
  const distributionChannelOptions = useSelector(() => adminDistributionChannels.getAsOptions())

  useEffect(() => {
    dispatch(adminBeneficiaryVettingStates.fetchListIfNeeded())
    dispatch(adminPopulationGroups.fetchListIfNeeded())
    dispatch(adminDistributionChannels.fetchListIfNeeded())
  }, [])

  React.useEffect(() => {
    async function fetchStaticObjects() {
      dispatch(adminCoreBusinessList.updatePaginationNumItemsPerPage(100))
      dispatch(adminCoreBusinessList.fetchListIfNeeded())
      dispatch(adminPublishedTagList.updatePaginationNumItemsPerPage(100))
      dispatch(adminPublishedTagList.updateListFilter({ published: true }))
      dispatch(adminPublishedTagList.fetchListIfNeeded())
    }
    fetchStaticObjects()
  }, [])

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false)
  }

  const setUrlSlug = (e) => {
    const { value } = e.target
    formik_props.setFieldValue('company.name', value)
    formik_props.setFieldValue('company.slug', urlSlug(value))
  }

  const renderDetailsSection = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormikInputField
          name="company.name"
          type="text"
          placeholder="Name"
          value={values.company && values.company.name}
          formik_props={formik_props}
          onChange={setUrlSlug}
          disabled={fieldsDisabled}
        />
        <Typography className={classes.fieldDescription} variant="caption" display="block">
          Record the official registered name from legal or registration documents. Cross-check spelling against provided certificates.
        </Typography>

        <Separator height={17} />
        <FormikInputField
          name="company.website"
          type="text"
          label="Website"
          value={values.company && values.company.website}
          formik_props={formik_props}
          disabled={fieldsDisabled}
        />
        <Typography className={classes.fieldDescription} variant="caption" display="block">
          Enter the organisation's official website address, if available. Use to verify information provided elsewhere in the form.
        </Typography>

        <Separator height={15} />
        <FormikDropdownField
          label="Core Business"
          name="company.core_businesses"
          options={coreBusinessOptions || []}
          formik_props={formik_props}
          disabled={fieldsDisabled}
        />
        <Typography className={classes.fieldDescription} variant="caption" display="block">
          Select the core focus area. Ensure alignment with stated programmes and target population.
        </Typography>

        <Separator height={17} />
        <FormikMultiSelectDropdownField
          name="company.target_population"
          label="Target Population"
          options={targetPopulationOptions || []}
          formik_props={formik_props}
          disabled={fieldsDisabled}
        />
        <Typography className={classes.fieldDescription} variant="caption" display="block">
          Tick all relevant beneficiary groups. Verify that these match the programmes and services listed.
        </Typography>

        <Separator height={17} />
        <FormikInputField
          name="company.programmes_and_services"
          type="text"
          label="Programmes & Services"
          value={values.company && values.company.programmes_and_services}
          formik_props={formik_props}
          disabled={fieldsDisabled}
        />
        <Typography className={classes.fieldDescription} variant="caption" display="block">
          Summarise the main programmes and services offered by the organisation. Use short, factual descriptions.        </Typography>

        <Separator height={17} />
        <FormikMultiSelectDropdownField
          name="company.distribution_channel"
          label="Distribution Channel"
          options={distributionChannelOptions || []}
          formik_props={formik_props}
          disabled={fieldsDisabled}
        />
        <Typography className={classes.fieldDescription} variant="caption" display="block">
          Select whether services are delivered directly to beneficiaries or through other organisations.        </Typography>
      </Grid>
    </Grid>
  )

  if (in_drawer) {
    return renderDetailsSection()
  }

  const formFieldErrorKeys = formik_props.errors
    ? Object.keys(formik_props.errors).filter((k) => !['non_field_errors', 'detail'].includes(k))
    : []
  const hasValidationErrors =
    formik_props.submitCount > 0 && formFieldErrorKeys.length > 0

  return (
    <Grid container spacing={2}>
      {hasValidationErrors && (
        <Grid item xs={12}>
          <Alert severity="error" style={{ marginBottom: 16 }}>
            Please complete all required fields.
          </Alert>
        </Grid>
      )}
      <Grid item xs={12} md={8} className={classes.companyAccordion}>

        <Accordion expanded={expanded === 'panel1'} onChange={handleChange('panel1')} variant="outlined" square>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="Details Dropdown" id="panel1bh-header">
            <Typography className={classes.heading}>Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {renderDetailsSection()}
          </AccordionDetails>
        </Accordion>

        <Accordion expanded={expanded === 'panel3'} onChange={handleChange('panel3')} variant="outlined" square>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="Non Profit & Department of Social Services Dropdown" id="panel3bh-header">
            <Typography className={classes.heading}>Non Profit & Department of Social Services</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <BeneficiaryVettingNpoDssFields formik_props={formik_props} fieldsDisabled={fieldsDisabled} />
          </AccordionDetails>
        </Accordion>

        <Accordion expanded={expanded === 'panel4'} onChange={handleChange('panel4')} variant="outlined" square>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="Resourcing & capacity Dropdown" id="panel4bh-header">
            <Typography className={classes.heading}>Resourcing & Capacity</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <BeneficiaryVettingResourcingCapacityFields formik_props={formik_props} fieldsDisabled={fieldsDisabled} />
          </AccordionDetails>
        </Accordion>

        <Accordion expanded={expanded === 'panel5'} onChange={handleChange('panel5')} variant="outlined" square>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="Feeding Dropdown" id="panel5bh-header">
            <Typography className={classes.heading}>Feeding</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <BeneficiaryVettingFeedingFields formik_props={formik_props} fieldsDisabled={fieldsDisabled} />
          </AccordionDetails>
        </Accordion>

        <BeneficiaryVettingHealthSafetyFields formik_props={formik_props} fieldsDisabled={fieldsDisabled} />
      </Grid>

      <Grid item xs={12} md={4} className={classes.logo}>
        <Card
          title="Status"
          content={
            get(formik_props.values, 'status') === 'approved' ? (
              <FormikInputField
                name="status"
                label="Status"
                formik_props={formik_props}
                disabled
                value="Approved"
              />
            ) : (
              <FormikDropdownField
                name="status"
                options={status_options || []}
                formik_props={formik_props}
                label="Status"
                disabled={fieldsDisabled}
              />
            )
          }
        />
        <Card
          title="SLA"
          content={
            <FormikSwitchField
              name="company.has_sla"
              label="SLA"
              formik_props={formik_props}
              color="primary"
              checked={get(company, 'has_sla', false)}
              disabled={fieldsDisabled}
            />
          }
        />
        <Card
          title="Consent Form"
          content={
            <FormikSwitchField
              name="company.has_consent_form"
              label="Consent Form"
              formik_props={formik_props}
              color="primary"
              checked={get(company, 'has_consent_form', false)}
              disabled={fieldsDisabled}
            />
          }
        />
        <Card
          title="Policy Documents"
          content={
            <FormikSwitchField
              name="company.has_policy_documents"
              label="Policy Documents"
              formik_props={formik_props}
              color="primary"
              checked={get(company, 'has_policy_documents', false)}
              disabled={fieldsDisabled}
            />
          }
        />
        <Card
          title="Additional Comments"
          content={
            <FormikInputField
              name="company.additionalComments"
              type="text"
              label="Record any further notes or relevant observations during vetting."
              formik_props={formik_props}
              value={get(company, 'additionalComments')}
              disabled={fieldsDisabled}
            />
          }
        />
      </Grid>

    </Grid>
  )
}
